import { Button, ContextMenu, Popover, TContextMenu } from '@ws/shared/components';
import {
  Eye16,
  EyeOff16,
  FilePlus16,
  FolderPlus16,
  MoreHorizontal16,
  Type16,
} from '@ws/shared/icons';
import { TEntity } from '@ws/shared/types';
import { isNotDesktop } from '@ws/shared/utils';
import { classes } from 'classifizer';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { ResolutionContext } from '../../../../context';
import { useCreateNodes } from '../../../../hooks';
import { TOnHideButtonClick, TOnWithTitleButtonClick } from '../types';

import styles from './RecursiveNodes.module.css';
import { useOutsideClick } from '@ws/shared/hooks';

interface INodeTools {
  node: TEntity;
  onHideButtonClick: TOnHideButtonClick;
  onWithTitleClick: TOnWithTitleButtonClick;
}

export function NodeTools({ node, onWithTitleClick, onHideButtonClick }: INodeTools) {
  const { resolution } = useContext(ResolutionContext);
  const [isOpen, setIsOpen] = useState(false);
  const showPopup = isNotDesktop(resolution);

  const wrapperRef = useRef(null);

  const toggleKebab = useCallback(() => setIsOpen((prevState) => !prevState), []);

  const { handleCreateNoteClick, handleCreateCollectionClick } = useCreateNodes({ entity: node });

  useOutsideClick(wrapperRef, () => {
    setIsOpen(false);
  });

  const items = useMemo<TContextMenu>(() => {
    const list = [];

    if (node.type === 'collection') {
      list.push(
        {
          leftIcon: <FilePlus16 />,
          onClick: () => {
            setIsOpen(false);
            handleCreateNoteClick();
          },
          label: 'Создать заметку',
          className: undefined,
        },
        {
          leftIcon: <FolderPlus16 />,
          onClick: () => {
            setIsOpen(false);
            handleCreateCollectionClick();
          },
          label: 'Создать коллекцию',
          className: undefined,
        },
      );
    }

    list.push(
      {
        leftIcon: <Type16 />,
        onClick: onWithTitleClick(node, node.meta.withTitle),
        label: node.meta.withTitle ? 'Скрыть заголовок' : 'Отобразить заголовок',
        className: node.meta.withTitle ? styles['tool-item_with-title_active'] : undefined,
      },
      {
        leftIcon: node.meta.isHidden ? <EyeOff16 /> : <Eye16 />,
        onClick: onHideButtonClick(node, node.meta.isHidden),
        label: `Отобразить ${node.type === 'note' ? 'заметку' : 'коллекцию'}`,
        className: node.meta.isHidden ? styles['tool-item_is-hidden_active'] : undefined,
      },
    );

    return list;
  }, [
    handleCreateCollectionClick,
    handleCreateNoteClick,
    node,
    onHideButtonClick,
    onWithTitleClick,
  ]);

  if (showPopup) {
    return (
      <div className={styles['popover__wrapper']}>
        <Popover.Container open={isOpen}>
          <Popover.Trigger onClick={toggleKebab}>
            <MoreHorizontal16 />
          </Popover.Trigger>

          <Popover.Content withoutPaddings>
            <div ref={wrapperRef}>
              <ContextMenu items={items} />
            </div>
          </Popover.Content>
        </Popover.Container>
      </div>
    );
  }

  return (
    <>
      {node.type === 'collection' && (
        <>
          <Button
            className={classes(styles['tool-item'], styles['with-title'])}
            onClick={handleCreateNoteClick}
            variant="shapeless"
            icon={<FilePlus16 />}
          />
          <Button
            className={classes(styles['tool-item'], styles['with-title'])}
            onClick={handleCreateCollectionClick}
            variant="shapeless"
            icon={<FolderPlus16 />}
          />
        </>
      )}

      <Button
        className={classes(
          styles['tool-item'],
          styles['with-title'],
          node.meta.withTitle && styles['tool-item_active'],
          node.meta.withTitle && styles['tool-item_with-title_active'],
        )}
        onClick={onWithTitleClick(node, node.meta.withTitle)}
        variant="shapeless"
        icon={<Type16 />}
      />
      <Button
        className={classes(
          styles['tool-item'],
          node.meta.isHidden && styles['tool-item_active'],
          node.meta.isHidden && styles['tool-item_is-hidden_active'],
        )}
        onClick={onHideButtonClick(node, node.meta.isHidden)}
        variant="shapeless"
        icon={node.meta.isHidden ? <EyeOff16 /> : <Eye16 />}
      />
    </>
  );
}
